import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { Typography } from '@telus-uds/components-web';
import { IotOBDYSplitter, IotRaven5CAN_USNon_Share, IotRaven5Non_Share, IotRavenDevice, IotTermMTM } from '../../../../utils/constant';
import { Thead, Tr, Th, Td } from './css';
import getFormattedPricing, { buildServiceMeta } from '../../utils';
import Row from './row';

const Table = ({
  type,
  isRenewal,
  viewType,
  ratePlans,
  hardware,
  setHardware,
  list,
  total,
  setTotal,
  geoTabNewPriceValid
}) => {
  const { hardware: hardwareTotal, accessories } = total;
  const valueTotal = type === 'HARDWARE' ? hardwareTotal : accessories;

  // Show a leading "Service" column when the section's header entry has
  // fields.isService === true. The flag lives on the header entry so the
  // table renders predictably regardless of row data. Defaults to false —
  // existing tables are unaffected.
  const headerEntry = list.find(item =>
    _.get(item, 'fields.isHeader', false)
  );

  const hasServiceColumn = Boolean(
    _.get(headerEntry, 'fields.isService', false)
  );
  const serviceMeta = buildServiceMeta(list, hasServiceColumn);

  const getHeader = headerList => (
    <Thead>
      <Tr>
        {hasServiceColumn && <Th>Service</Th>}
        {headerList.map(item => (
          <Th>{_.get(item, 'fields.title', '')}</Th>
        ))}
      </Tr>
    </Thead>
  );

  useEffect(() => {
    const mtmOBDPrice = [IotRaven5Non_Share, IotRaven5CAN_USNon_Share].reduce((acc, current)=>{
      if(!ratePlans[current]) return acc
      const ratePlan = ratePlans[current][IotRavenDevice]
      if(ratePlan && ratePlan.term === IotTermMTM){
        return acc + ratePlan.quantity || 0
      } else 
      return acc
    }, 0)
    const grandTotal = Object.keys(hardware).reduce(
      (acc, current) => {
        const { unitPrice, additionalQuantity, isAccessory, OBDQuantity = 0 } = hardware[
          current
        ];
        if (!isAccessory && unitPrice && additionalQuantity)
          return {
            ...acc,
            hardware: acc.hardware + unitPrice * +additionalQuantity
          };
        if (isAccessory && unitPrice && additionalQuantity) {
          if (current === IotOBDYSplitter) {
            return {
              ...acc,
              accessories: acc.accessories + (unitPrice * +OBDQuantity) + (unitPrice * mtmOBDPrice)
            };
          } else {
            return {
              ...acc,
              accessories: acc.accessories + unitPrice * +additionalQuantity
            };
          }
        }
        return acc;
      },
      { hardware: 0, accessories: 0 }
    );
    setTotal(previousValue => ({ ...previousValue, ...grandTotal }));
  }, [hardware, geoTabNewPriceValid]);

  const baseTotalColSpan = isRenewal || type === 'ACCESSORY' ? 3 : 5;

  return (
    <table>
      {list.map((hardwareItem, index) => {
        if (_.get(hardwareItem, 'fields.isHeader', false)) {
          return getHeader(
            _.get(
              hardwareItem,
              `fields.${isRenewal ? 'headerListRenewal' : 'headerList'}`,
              []
            )
          );
        }
        const { shouldRender, rowSpan } = serviceMeta[index];
        return (
          <Row
            key={_.get(hardwareItem, 'sys.id', '')}
            rowData={hardwareItem}
            isRenewal={isRenewal}
            viewType={viewType}
            ratePlans={ratePlans}
            hardware={hardware}
            setHardware={setHardware}
            hasServiceColumn={hasServiceColumn}
            serviceLabel={_.get(hardwareItem, 'fields.productSectionType', '')}
            renderServiceCell={shouldRender}
            serviceRowSpan={rowSpan}
          />
        );
      })}
      <Tr style={{ background: '#f4f4f4' }}>
        <Td colSpan={hasServiceColumn ? baseTotalColSpan + 1 : baseTotalColSpan}>
          <Typography variant={{ bold: true }}>Total</Typography>
        </Td>
        <Td>
          <Typography variant={{ bold: true }}>{getFormattedPricing(valueTotal)}</Typography>
        </Td>
      </Tr>
    </table>
  );
};

Table.propTypes = {
  type: PropTypes.string.isRequired,
  isRenewal: PropTypes.bool.isRequired,
  viewType: PropTypes.string.isRequired,
  ratePlans: PropTypes.arrayOf(PropTypes.object).isRequired,
  hardware: PropTypes.arrayOf(PropTypes.object).isRequired,
  setHardware: PropTypes.func.isRequired,
  list: PropTypes.arrayOf(PropTypes.object).isRequired,
  total: PropTypes.shape({
    ratePlan: PropTypes.number,
    subscriberList: PropTypes.number,
    additionalServices: PropTypes.number,
    addons: PropTypes.number,
    hardware: PropTypes.number,
    accessories: PropTypes.number
  }).isRequired,
  setTotal: PropTypes.func.isRequired
};

export default Table;
